/**
 * AT-Render-Layer für rich_description.
 *
 * KEIN DB-Eingriff: die 49 Produkt-JSONs bleiben unverändert. Auf CH ist
 * diese Funktion die Identität (byte-identisch zu heute). Auf AT werden
 * ausschliesslich die identifizierten Template-Felder gezielt umgeschrieben:
 *
 *  - `intro`: 'Preise[n] der Schweiz' → 'Preise[n] Österreichs'
 *  - `howItWorks[step===2].text`: durch AT-Zahlungstext ersetzt
 *  - `benefits[title==='Günstigste Preise der Schweiz']`: Title/Text → Österreich
 *  - `whyBigFollow[*].text` mit 'Tausende Kunden in der Schweiz und weltweit':
 *    → 'Tausende Kunden in Österreich und weltweit …'
 *  - `faq[*].answer` mit 'TWINT, Google Pay, Apple Pay': → AT-Methoden
 *
 * NICHT angetastet (auch für AT wahr / produktspezifisch):
 *  - whyBigFollow-Karten 'Schweizer Qualität', 'Schweizer Zuverlässigkeit',
 *    'Schweizer Unternehmen mit Sitz in der Schweiz'
 *  - alle produktspezifischen FAQ-Antworten, Tipps, forWhom-Einträge
 *
 * AT-Zahlungsmethoden werden aus `paymentMethodsLabel('at')` in
 * src/lib/paymentMethods.ts abgeleitet (Single Source of Truth mit den
 * tatsächlich verfügbaren AT-Kassenmethoden und dem Trust-Badge).
 */
import type { Storefront } from '@/hooks/useStorefront';

/** AT-Zahlungsmethoden — Reihenfolge = Trust-Badge-Reihenfolge. */
export const AT_PAYMENT_METHODS_TEXT = 'Kreditkarte, PayPal, Apple Pay oder Klarna';
/** Für FAQ 'Wir akzeptieren …, …' (mit Klammer-Präfix Visa/Mastercard). */
export const AT_PAYMENT_METHODS_INLINE = 'PayPal, Apple Pay, Klarna';

const HOW_IT_WORKS_STEP2_AT_TEXT =
  'Bezahle bequem und sicher mit ' +
  AT_PAYMENT_METHODS_TEXT +
  '. Alle Zahlungen sind SSL-verschlüsselt.';

function transformIntroAT(intro: string): string {
  return intro
    .replace(/Preisen der Schweiz/g, 'Preisen Österreichs')
    .replace(/Preise der Schweiz/g, 'Preise Österreichs');
}

function transformHowItWorksAT(steps: any[]): any[] {
  if (!Array.isArray(steps)) return steps;
  return steps.map((s) =>
    s && s.step === 2 ? { ...s, text: HOW_IT_WORKS_STEP2_AT_TEXT } : s,
  );
}

function transformBenefitsAT(items: any[]): any[] {
  if (!Array.isArray(items)) return items;
  return items.map((b) => {
    if (!b) return b;
    if (b.title === 'Günstigste Preise der Schweiz') {
      return {
        ...b,
        title: 'Günstigste Preise Österreichs',
        text: typeof b.text === 'string'
          ? b.text.replace('Preise in der Schweiz', 'Preise in Österreich')
          : b.text,
      };
    }
    return b;
  });
}

function transformWhyBigFollowAT(items: any[]): any[] {
  if (!Array.isArray(items)) return items;
  return items.map((w) => {
    if (!w || typeof w.text !== 'string') return w;
    if (w.text.startsWith('Tausende Kunden in der Schweiz und weltweit')) {
      return {
        ...w,
        text: w.text.replace(
          'Tausende Kunden in der Schweiz und weltweit',
          'Tausende Kunden in Österreich und weltweit',
        ),
      };
    }
    return w;
  });
}

function transformFaqAT(items: any[]): any[] {
  if (!Array.isArray(items)) return items;
  return items.map((f) => {
    if (!f || typeof f.answer !== 'string') return f;
    // Bekannte Zahlungsmethoden-Sequenz.
    if (f.answer.includes('TWINT, Google Pay, Apple Pay')) {
      return {
        ...f,
        answer: f.answer.replace('TWINT, Google Pay, Apple Pay', AT_PAYMENT_METHODS_INLINE),
      };
    }
    return f;
  });
}

/**
 * Wendet AT-Render-Transformationen auf rich_description an.
 * Für alle nicht-AT Storefronts (inkl. CH): Identität → byte-identisch.
 */
export function sanitizeRichForStorefront(
  rich: any,
  sf: Storefront,
): any {
  if (sf !== 'at' || !rich || typeof rich !== 'object') return rich;
  const out: any = { ...rich };
  if (typeof out.intro === 'string') out.intro = transformIntroAT(out.intro);
  if (out.howItWorks) out.howItWorks = transformHowItWorksAT(out.howItWorks);
  if (out.benefits) out.benefits = transformBenefitsAT(out.benefits);
  if (out.whyBigFollow) out.whyBigFollow = transformWhyBigFollowAT(out.whyBigFollow);
  if (out.faq) out.faq = transformFaqAT(out.faq);
  return out;
}